import { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import {
  URL_DEFAULT_SCHEMES,
  validateUrl,
} from '../lib/urlValidate';

const SAMPLES: ReadonlyArray<{ label: string; value: string }> = [
  { label: '正常 https', value: 'https://example.com/api/v1?id=42#frag' },
  { label: '默认端口', value: 'https://example.com:443/path' },
  { label: '本机', value: 'http://localhost:3000' },
  { label: 'mailto', value: 'mailto:foo@example.com' },
  { label: '危险 javascript', value: 'javascript:alert(1)' },
  { label: '没有协议', value: 'example.com/path' },
  { label: 'userinfo', value: 'https://user:pw@example.com' },
];

const STRUCT_KEYS: ReadonlyArray<{ key: keyof StructPart; label: string }> = [
  { key: 'protocol', label: 'protocol' },
  { key: 'username', label: 'username' },
  { key: 'password', label: 'password' },
  { key: 'host', label: 'host' },
  { key: 'hostname', label: 'hostname' },
  { key: 'port', label: 'port' },
  { key: 'pathname', label: 'pathname' },
  { key: 'search', label: 'search' },
  { key: 'hash', label: 'hash' },
  { key: 'origin', label: 'origin' },
];

interface StructPart {
  protocol: string;
  username: string;
  password: string;
  host: string;
  hostname: string;
  port: string;
  pathname: string;
  search: string;
  hash: string;
  origin: string;
}

/**
 * `/tools/validate/url-validate` — URL syntax validator.
 *
 * Sister tool to `network/url-parse`: that one tells you "what's
 * inside this legal URL"; this one tells you "is this URL legal,
 * and if not, why".
 */
export default function UrlValidateTool(): JSX.Element {
  const [input, setInput] = useState<string>(SAMPLES[0].value);
  const [requireTld, setRequireTld] = useState(false);
  const [schemes, setSchemes] = useState<string[]>([...URL_DEFAULT_SCHEMES]);

  const result = useMemo(
    () => validateUrl(input, { schemes, requireTld }),
    [input, schemes, requireTld],
  );

  const toggleScheme = (s: string): void => {
    setSchemes((prev) =>
      prev.includes(s) ? prev.filter((p) => p !== s) : [...prev, s],
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="url-validate"
        category="validate"
        description="WHATWG URL 构造器 + 协议白名单 + 默认端口检测 + userinfo / 内部空白警告。与 network/url-parse 的边界：那个工具假定 URL 合法做拆解，本工具回答“合法吗，为什么”。"
      />

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            示例
          </span>
        </header>
        <div className="flex flex-wrap gap-1.5">
          {SAMPLES.map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => setInput(s.value)}
              className="rounded border border-line px-2 py-0.5 text-[12px] text-ink-muted transition hover:border-accent/40 hover:text-ink"
              title={s.value}
            >
              {s.label}
            </button>
          ))}
        </div>
      </section>

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            URL
          </span>
          <ResultBadge ok={result.ok} hasWarnings={result.issues.some((i) => i.severity === 'warn')} />
        </header>
        <input
          type="text"
          spellCheck={false}
          autoComplete="off"
          className="input font-mono text-[13px]"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="https://example.com/path?q=1"
        />

        <div className="flex flex-wrap items-center gap-3 text-[12px] text-ink-muted">
          <label className="inline-flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={requireTld}
              onChange={(e) => setRequireTld(e.target.checked)}
            />
            要求顶级域（拒绝 http://localhost）
          </label>
          <span>·</span>
          <span>允许的协议：</span>
          {URL_DEFAULT_SCHEMES.map((s) => (
            <label
              key={s}
              className="inline-flex cursor-pointer items-center gap-1"
            >
              <input
                type="checkbox"
                checked={schemes.includes(s)}
                onChange={() => toggleScheme(s)}
              />
              {s}
            </label>
          ))}
        </div>
      </section>

      {result.parts && (
        <section className="card-pad flex flex-col gap-1">
          <header className="text-[11px] uppercase tracking-wide text-ink-dim">
            结构（WHATWG URL 字段）
          </header>
          <ul className="grid gap-x-4 gap-y-0.5 text-[12.5px] sm:grid-cols-2">
            {STRUCT_KEYS.map(({ key, label }) => (
              <li key={key} className="flex items-baseline gap-2">
                <span className="w-20 shrink-0 text-ink-muted">{label}</span>
                <code className="font-mono text-ink">
                  {result.parts![key] || (
                    <em className="text-ink-dim">(空)</em>
                  )}
                </code>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card-pad flex min-h-0 flex-1 flex-col gap-1">
        <header className="text-[11px] uppercase tracking-wide text-ink-dim">
          诊断（{result.issues.length}）
        </header>
        {result.issues.length === 0 ? (
          <p className="text-[12.5px] text-emerald-600 dark:text-emerald-400">
            未检测到任何问题。
          </p>
        ) : (
          <ul className="divide-y divide-line/40">
            {result.issues.map((i, idx) => (
              <li key={idx} className="flex items-start gap-2 px-1 py-1.5">
                {i.severity === 'error' ? (
                  <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
                ) : (
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] text-ink">{i.message}</div>
                  <div className="text-[10.5px] font-mono text-ink-dim">{i.code}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ResultBadge({
  ok,
  hasWarnings,
}: {
  ok: boolean;
  hasWarnings: boolean;
}): JSX.Element {
  if (!ok) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[11px] text-red-600 dark:text-red-400">
        <XCircle className="h-3 w-3" />
        无效
      </span>
    );
  }
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]',
        hasWarnings
          ? 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400'
          : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
      )}
    >
      <CheckCircle2 className="h-3 w-3" />
      {hasWarnings ? '有效（含警告）' : '有效'}
    </span>
  );
}
